import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { MarketplaceEmailSender } from '@spike/email-marketing-model';
import AlertMessage from 'components/UI/V3/AlertMessage/AlertMessage';
import Button from 'components/UI/V3/Button/Button';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import EmailSenderSectionHeader from '../EmailSenderSectionHeader';
import { useEmailSenderData } from '../hooks/useEmailSenderData';
import { SHOW_CUDDLES_DEFAULT_ESCAPE } from './senderFeatureFlags';
import sharedStyles from './states.module.scss';

interface PendingStateProps {
    sender: MarketplaceEmailSender;
    /** Opens the edit modal pre-filled with the current address. */
    onEdit: () => void;
    /** Opens the "Use Cuddles default?" confirm modal. */
    onEscape: () => void;
}

// Seconds the "I've Confirmed" button stays locked after a click. Short
// enough to feel reactive, long enough to absorb double-taps and give SES a
// moment to propagate if the user just clicked the AWS link.
const REFRESH_LOCK_SECONDS = 10;
// Refresh attempts that keep the state at `pending` before we surface the
// "still pending?" hint about checking spam / editing the address.
const REFRESH_HINT_THRESHOLD = 3;

/**
 * Pending verification — the user has submitted an email, BE registered it
 * with SES, and AWS sent a verification email. The user has to click the
 * link in their inbox, then come back here and press "I've Confirmed".
 *
 * Layout follows the designer's mockup 3: the AlertMessage holds both the
 * instructions copy and the action buttons (I've Confirmed + Edit Email)
 * so the user reads "do X, then click Y" without their eyes jumping rows.
 * The escape link lives below the card as prose.
 */
const PendingState: FunctionComponent<PendingStateProps> = ({ sender, onEdit, onEscape }) => {
    const { saving, refreshSender } = useEmailSenderData();
    const [locked, setLocked] = useState<boolean>(false);
    const [refreshCount, setRefreshCount] = useState<number>(0);
    const lockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
        };
    }, []);

    const handleRefresh = async () => {
        if (locked || saving) return;
        await refreshSender();
        setRefreshCount(previous => previous + 1);
        setLocked(true);
        if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
        lockTimerRef.current = setTimeout(() => setLocked(false), REFRESH_LOCK_SECONDS * 1000);
    };

    let primaryLabel: string;
    if (saving) primaryLabel = 'Checking…';
    else if (locked) primaryLabel = 'Checked just now';
    else primaryLabel = "I've Confirmed";

    const showHint = refreshCount >= REFRESH_HINT_THRESHOLD;

    // Copy is FE-side (overrides BE's `instructions` field) because the
    // mockup's wording is product-curated and we don't want the BE message
    // to drift over time.
    const instructionsCopy = `Check ${
        sender.senderEmail ?? 'your inbox'
    } for the AWS verification email and click in the "I've Confirmed"`;

    return (
        <div className={sharedStyles.container}>
            <EmailSenderSectionHeader />

            <div className={sharedStyles.content}>
                <div className={sharedStyles.emailRow}>
                    <p className={sharedStyles.email}>{sender.senderEmail}</p>
                    <span className={`${sharedStyles.badge} ${sharedStyles.badgePending}`}>Pending Verification</span>
                </div>

                <AlertMessage
                    icon={faInfoCircle}
                    iconColor="#BAA997"
                    bdColor="#BCB8AE"
                    bgColor="#F8F5F1"
                >
                    <span className={sharedStyles.instructionsCopy}>{instructionsCopy}</span>
                    <div className={sharedStyles.buttonsInsideAlert}>
                        <Button
                            size="medium"
                            label={primaryLabel}
                            variant="green"
                            onClick={handleRefresh}
                            disabled={saving || locked}
                        />
                        <Button
                            size="medium"
                            label="Edit Email"
                            variant="black"
                            onClick={onEdit}
                            disabled={saving}
                        />
                    </div>
                </AlertMessage>

                {showHint && (
                    <p className={sharedStyles.hint}>
                        Still pending? Check your spam folder, or edit the email address.
                    </p>
                )}
            </div>

            {SHOW_CUDDLES_DEFAULT_ESCAPE && (
                <p className={sharedStyles.escapeLine}>
                    Don&apos;t want a custom sender?{' '}
                    <button
                        type="button"
                        className={sharedStyles.escapeLink}
                        onClick={onEscape}
                        disabled={saving}
                    >
                        Use the Cuddles default instead
                    </button>
                </p>
            )}
        </div>
    );
};

export default PendingState;
